import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type {
  DiagnosticReport,
  Observation,
  RequestGroup,
} from "@curai/js-fhir-tools";
import type { ObservationValue } from "@curai/js-fhir-tools";

export interface ReportComment {
  comment: string;
  author?: string;
  createdAt: string;
}

export interface ReportResultReferenceRange {
  rangeLow?: number;
  rangeHigh?: number;
  rangeText?: string;
}

export interface LabFacility {
  facilityName?: string;
  facilityContactName?: string;
  address?: ReportAddress;
}

export interface DxReportViewTestResult {
  curaiObservationId: string;
  resultName: string;
  resultRawValue?: ObservationValue | null;
  resultFlag?: (typeof Observation.Interpretation.values)[keyof typeof Observation.Interpretation.values];
  resultComments: ReportComment[];
  resultReferenceRange?: ReportResultReferenceRange;
  performingLabFacility: LabFacility;
  resultStatus: (typeof Observation.Status)[keyof typeof Observation.Status];
}

export interface DxReportViewTest {
  curaiServiceRequestId?: string;
  testResults: DxReportViewTestResult[];
  testName?: string;
  orderingClinician?: string;
}

export interface ReportAddress {
  streetLines: string[];
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface ReportPatient {
  vendorPatientId: string;
  curaiPatientId?: string;
  adminGender?: string;
  dateOfBirth?: string;
  genderIdentity?: string;
  phone?: string;
  name?: string;
  address?: ReportAddress;
}

export interface ReportPdf {
  pdfSource: string;
  fileName?: string;
  createdAt?: string;
  fileLocation?: string;
}

export interface ReportOrderingClinician {
  curaiClinicianId?: string;
  vendorClinicianId?: string;
  name?: string;
  address?: ReportAddress;
  phone?: string;
  npi?: string;
}

export interface DxReportViewResponse {
  tests: DxReportViewTest[];
  curaiDiagnosticReportId: string;
  curaiRequestGroupId?: string;
  labNetwork: (typeof RequestGroup.LAB_NETWORKS)[keyof typeof RequestGroup.LAB_NETWORKS];
  comments: ReportComment[];
  patient: ReportPatient;
  availablePdfs: ReportPdf[];
  accessionNumber?: string;
  curaiOrderNumber?: string;
  resultsStatus: (typeof DiagnosticReport.Status)[keyof typeof DiagnosticReport.Status];
  orderingClinician?: ReportOrderingClinician;
  receivedAt: string;
  issuedAt: string;
  effectiveAt: string;
}

export const useDxReportView = ({
  patientId,
  diagnosticReportId,
}: {
  patientId: string;
  diagnosticReportId: string;
}) => {
  return useApi<DxReportViewResponse>({
    queryOptions: {
      enabled: Boolean(diagnosticReportId),
      queryKey: QueryKeys.Patient.MedicalRecords.Labs.DX_REPORT_VIEW(
        patientId,
        diagnosticReportId,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/labs/results/${diagnosticReportId}/view`,
          {
            params: {
              diagnosticReportId,
            },
          },
        );
        return response?.data;
      },
    },
  });
};
